import {
  deleteLocations,
  getLocations,
  patchLocations,
  postLocations,
} from "./generated/calls/locations/locations";
import type {
  LocationsInsert as LocationInsertDTO,
  Locations as LocationDTO,
  LocationsUpdate as LocationUpdateDTO,
} from "./generated/models";
import { buildHeaders, headersToObject } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResult, APIResultNoContent } from "./types";

export type { LocationDTO, LocationInsertDTO, LocationUpdateDTO };

/**
 * Fetches all fleet locations from the API.
 *
 * Returns an API result containing the fetched locations, or a controlled
 * failure if the request could not be completed.
 */
export async function fetchFleetLocations(): Promise<APIResult<LocationDTO[]>> {
  try {
    const headers = buildHeaders();
    const locationsFetchRes = await getLocations(undefined, { headers });

    switch (locationsFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        return {
          success: true,
          data: locationsFetchRes.data,
        };
      default:
        throw postgrestError("Fetch failed", locationsFetchRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

export async function deleteLocation(
  locationExternalID: string,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const deleteLocationRes = await deleteLocations(
      {
        id: postgrestFilter(locationExternalID),
      },
      {
        headers,
      },
    );

    switch (deleteLocationRes.status) {
      case 200:
        return {
          success: true,
        };

      default:
        throw postgrestError("Delete failed", deleteLocationRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

export async function createLocation(
  locationData: LocationInsertDTO,
): Promise<APIResult<LocationDTO>> {
  try {
    const headers = buildHeaders();
    const createLocationRes = await postLocations(locationData, undefined, {
      headers: headersToObject(headers),
    });

    switch (createLocationRes.status) {
      case 201: {
        // An insert returns the rows it created, so a single insert returns a
        // one-element array.
        const created = createLocationRes.data[0];

        if (!created) {
          throw new Error("Create succeeded but returned no location");
        }

        return {
          success: true,
          data: created,
        };
      }

      default:
        throw postgrestError("Create failed", createLocationRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}

export async function updateLocation(
  locationId: string,
  locationData: LocationUpdateDTO,
): Promise<APIResultNoContent> {
  try {
    const headers = buildHeaders();
    const updateLocationRes = await patchLocations(
      locationData,
      { id: postgrestFilter(locationId) },
      { headers: headersToObject(headers) },
    );

    switch (updateLocationRes.status) {
      case 200:
        return {
          success: true,
        };

      default:
        throw postgrestError("Update failed", updateLocationRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}
